/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
 */

import { FormActionFooter, useFormFeedback } from "../ui/FormActionFooter.js";
import type { VNode } from "preact";
import { useState } from "preact/hooks";
import { Header } from "../ui/Header.js";
import { Button } from "../ui/Button.js";
import { PasswordInput } from "../ui/PasswordInput.js";
import { useTranslation } from "../context/translation.js";

export function AdminAccountCredentialsScreen({
  instanceId,
  onBack,
  onReset,
}: {
  instanceId: string;
  onBack: () => void;
  onReset: (password: string) => Promise<void>;
}): VNode {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const {
    rawError: error,
    setRawError: setError,
    errorMsg: validation,
    setErrorMsg: setValidation,
    feedbackId,
    fieldProps,
    fieldError,
  } = useFormFeedback();
  const [pending, setPending] = useState(false);

  const submit = async (event: Event) => {
    event.preventDefault();
    setValidation("");
    setError(undefined);
    if (password.length < 8) {
      setValidation(
        t`Password must be at least 8 characters long.`,
        "admin-reset-password",
      );
      return;
    }
    if (password !== confirm) {
      setValidation(
        t`Passwords do not match. Please re-type your password.`,
        "admin-reset-confirm",
      );
      return;
    }
    setPending(true);
    try {
      await onReset(password);
      setPassword("");
      setConfirm("");
    } catch (cause) {
      setError(cause);
    } finally {
      setPending(false);
    }
  };

  return (
    <div class="max-w-2xl space-y-5">
      <Header
        title={t`Reset merchant account password`}
        subtitle={t`Set a new password for merchant account ${instanceId}.`}
        onBack={onBack}
        backLabel={t`Merchant account`}
      />
      <div class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">{t`The account’s existing password will stop working. Existing login tokens remain governed by the backend’s token policy.`}</div>

      <form
        onSubmit={(event) => void submit(event)}
        class="space-y-5 rounded-xl border border-gray-200 bg-white p-5"
      >
        <div>
          <PasswordInput
            {...fieldProps("admin-reset-password")}
            id="admin-reset-password"
            label={t`New password`}
            value={password}
            onInput={setPassword}
            autoComplete="new-password"
            required
            showRequiredIndicator={false}
            minLength={8}
          />
          {fieldError("admin-reset-password")}
        </div>
        <div>
          <PasswordInput
            {...fieldProps("admin-reset-confirm")}
            id="admin-reset-confirm"
            label={t`Confirm new password`}
            value={confirm}
            onInput={setConfirm}
            autoComplete="new-password"
            required
            showRequiredIndicator={false}
            minLength={8}
          />
          {fieldError("admin-reset-confirm")}
        </div>
        <FormActionFooter
          error={error}
          validationError={validation}
          feedbackId={feedbackId}
          pending={pending}
          title={t`Could not reset password`}
        >
          <Button
            variant="secondary"
            onClick={onBack}
            disabled={pending}
          >{t`Cancel`}</Button>
          <Button type="submit" isLoading={pending}>{t`Reset password`}</Button>
        </FormActionFooter>
      </form>
    </div>
  );
}
